const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]'];
const CONTENT_MODES = ['fit', 'fill', 'center', 'legacy'];

/**
 * Build the AR runtime configuration for one device platform ('ios' or
 * 'android') from a `GET /v2/experience/{identifier}` response, the contract
 * the native apps use. Every video with an anchor for this platform is kept,
 * and each such anchor becomes an image target keyed by its anchor ID, so all
 * target-to-video associations survive. Media URLs are the original signed
 * resources; nothing is prepared or converted.
 */
export function buildRuntimeConfig(experience, platform) {
  if (!experience || !Array.isArray(experience.videos)) throw new Error('The experience response is incomplete.');
  const videos = [];
  const targets = [];
  const targetIds = new Set();
  for (const video of experience.videos) {
    const anchors = video?.anchors?.images;
    if (!Array.isArray(anchors)) throw new Error('The experience response has an invalid video.');
    const deviceAnchors = anchors.filter(anchor => anchorPlatforms(anchor).includes(platform));
    if (deviceAnchors.length === 0) continue;
    videos.push({
      id: requiredId(video.id, 'video'),
      url: mediaUrl(video.resource?.url, 'video'),
      contentType: requiredString(video.resource?.contentType, 'video content type'),
      // The native players loop every video (AVPlayerLooper on iOS 1.2.5).
      loop: true,
    });
    for (const anchor of deviceAnchors) {
      const id = requiredId(anchor.id, 'anchor');
      if (targetIds.has(id)) throw new Error('The experience response repeats an image anchor.');
      targetIds.add(id);
      targets.push({
        id,
        videoId: video.id,
        imageUrl: requiredString(anchor.image?.url, 'anchor image URL'),
        imageContentType: requiredString(anchor.image?.contentType, 'anchor image content type'),
        ...presentationTraits(anchor.traits),
      });
    }
  }
  if (new Set(videos.map(video => video.id)).size !== videos.length) {
    throw new Error('The experience response repeats a video.');
  }
  if (targets.length === 0) throw new Error('This experience has no AR images for this device.');
  return {videos, targets};
}

/** The API serializes platforms as "iOS" and "android". */
function anchorPlatforms(anchor) {
  const platforms = anchor?.traits?.platforms;
  return Array.isArray(platforms) ? platforms.map(value => String(value).toLowerCase()) : [];
}

/** Unset traits use the native defaults: the legacy content mode and a rectangular plane. */
function presentationTraits(traits) {
  const contentMode = traits.contentMode ?? 'legacy';
  if (!CONTENT_MODES.includes(contentMode) || !Number.isFinite(traits.priority) ||
      (traits.isRound !== null && traits.isRound !== undefined && typeof traits.isRound !== 'boolean')) {
    throw new Error('The experience response has invalid video presentation traits.');
  }
  return {
    contentMode,
    isRound: traits.isRound ?? false,
    priority: traits.priority,
  };
}

function requiredId(value, description) {
  return requiredString(value, `${description} ID`);
}

function requiredString(value, description) {
  if (typeof value !== 'string' || value.length === 0) throw new Error(`The experience response has no ${description}.`);
  return value;
}

/** Signed media must use HTTPS; plain HTTP is accepted only from a localhost development host. */
export function mediaUrl(value, description) {
  let url;
  try {
    url = new URL(requiredString(value, `${description} URL`));
  } catch (error) {
    if (error instanceof TypeError) throw new Error(`The experience response has an invalid ${description} URL.`);
    throw error;
  }
  const local = LOCAL_HOSTS.includes(url.hostname);
  if (url.username || url.password || (url.protocol !== 'https:' && !(local && url.protocol === 'http:'))) {
    throw new Error(`The experience ${description} URL must use HTTPS.`);
  }
  return url.href;
}
